import { queryOptions, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { ONE_HOUR_IN_MS } from '@/common/constants';
import { visualizationQueryKeys } from '@/features/visualizations/queryKeys';
import { headerToPropertySelector } from '@/features/visualizations/utils/commonLayoutUtils';
import { getAndValidateTimelineLayout } from '@/features/visualizations/utils/timelineLayout';

const getTimelineLayoutQueryOptions = (
  viewId: number,
  visualizationId: number,
  options?: Partial<UseQueryOptions<TimelineLayoutDTO, Error, TimelineLayoutDTO>>,
) =>
  queryOptions({
    queryKey: visualizationQueryKeys.timelineLayout.detail(viewId, visualizationId),
    queryFn: () => getAndValidateTimelineLayout(viewId, visualizationId),
    retry: false,
    refetchOnMount: false,
    staleTime: ONE_HOUR_IN_MS,
    enabled: Boolean(viewId) && Boolean(visualizationId),
    ...options,
  });

export const useTimelineLayout = (
  viewId: number,
  visualizationId: number,
  options?: Partial<UseQueryOptions<TimelineLayoutDTO, Error, TimelineLayoutDTO>>,
) => {
  return useQuery(getTimelineLayoutQueryOptions(viewId, visualizationId, options));
};

export const useTimelineLayoutHeader = (viewId: number, visualizationId: number) => {
  return useQuery({
    ...getTimelineLayoutQueryOptions(viewId, visualizationId),
    select: (data) => ({
      header1: headerToPropertySelector(data.header1),
      header2: headerToPropertySelector(data.header2),
      itemLevelId: data.itemLevelId,
    }),
  });
};

export const useTimelineLayoutLabelBy = (viewId: number, visualizationId: number) => {
  return useQuery({
    ...getTimelineLayoutQueryOptions(viewId, visualizationId),
    select: (data) => ({
      labelByHeader1: headerToPropertySelector(data.labelByHeader1),
      labelByHeader2: headerToPropertySelector(data.labelByHeader2),
    }),
  });
};
